import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import OnboardingStageTimeline from './OnboardingStageTimeline';
import SharedTaskCell from './SharedTaskCell';
import WorkflowConnector, { MergePointDiamond, AddTaskButton } from './WorkflowConnector';
import './OnboardingWorkflow.css';

const PURE_AUTOMATION_TYPES = new Set(['scheduled_email']);
const NEW_HIRE_AUTOMATION_TYPES = new Set(['document_request', 'document_send', 'form_collect', 'meeting_schedule']);

/**
 * Main workflow view component that renders the vertical branching workflow
 * 
 * Implements:
 * - Same-day task grouping into shared cells
 * - Branching: pure automations (left) vs manual and new-hire tasks (right)
 * - Merge points after parallel branches
 */
const OnboardingWorkflowView = ({
    hire,
    stages = ['Before Day One', 'Day One', 'First Week', 'First Month', '90-Day Review'],
    activeStage,
    onStageChange,
    teamMembers = [],
    externalContacts = [],
    onTaskAction,
    onAddTask,
    onAddAutomatedTask,
    onAddHumanTask,
    onEditTask,
    onDeleteTask,
    canManageTasks = true,
    canToggleTaskCompletion = () => true
}) => {
    const { t } = useTranslation('onboarding');
    const normalizeDateKey = (rawDate, executionMode) => {
        if (!rawDate) {
            return executionMode === 'immediate' ? '0000-00-00_immediate' : '9999-99-99_no_date';
        }

        if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
            return rawDate;
        }

        const parsed = new Date(rawDate);
        if (Number.isNaN(parsed.getTime())) {
            return '9999-99-99_no_date';
        }

        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const sortByDate = (a, b) => {
        const keyA = normalizeDateKey(a?.dueDate, a?.executionMode);
        const keyB = normalizeDateKey(b?.dueDate, b?.executionMode);
        if (keyA === keyB) {
            return (a?.createdAt || 0) - (b?.createdAt || 0);
        }
        if (keyA === '0000-00-00_immediate') return -1;
        if (keyB === '0000-00-00_immediate') return 1;
        if (keyA === '9999-99-99_no_date') return 1;
        if (keyB === '9999-99-99_no_date') return -1;
        return keyA.localeCompare(keyB);
    };

    // Get all tasks for active stage as a flat array
    const stageTasks = useMemo(() => {
        if (!hire?.tasks?.[activeStage]) {
            return [];
        }

        const stageData = hire.tasks[activeStage];

        // Check if it's the new workflow format
        if (stageData.workflow?.tasks) {
            return Object.entries(stageData.workflow.tasks).map(([id, task]) => ({
                id,
                ...task
            })).sort(sortByDate);
        }

        // Legacy format: flat task list
        return Object.entries(stageData)
            .filter(([key]) => key !== 'workflow')
            .map(([id, task]) => ({
                id,
                ...task
            }))
            .sort(sortByDate);
    }, [hire, activeStage, sortByDate]);

    const getAutomationType = (task = {}) => {
        if (task.automationType === 'document') return 'document_send';
        if (task.automationType === 'form') return 'form_collect';
        if (task.automationType === 'meeting') return 'meeting_schedule';
        if (task.automationType) return task.automationType;
        if (task.type === 'documentRequest' || task.portalUploadRequired || task.documentUploadLink) return 'document_request';
        if (task.formConfig || task.formLink) return 'form_collect';
        if (task.meetingConfig) return 'meeting_schedule';
        if ((task.type === 'automated' || task.type === 'document') && Array.isArray(task.attachments) && task.attachments.length > 0) return 'document_send';
        if (task.type === 'automated') return 'scheduled_email';
        return '';
    };

    // Determine if a task is a pure system automation.
    const isAutomatedTask = (task) => {
        const automationType = getAutomationType(task);
        if (NEW_HIRE_AUTOMATION_TYPES.has(automationType)) return false;
        return PURE_AUTOMATION_TYPES.has(automationType) ||
            ((task.assigneeCategory === 'automations' || task.assignee === 'System' || task.assigneeName === 'System (Auto)') &&
                !NEW_HIRE_AUTOMATION_TYPES.has(automationType));
    };

    // Group tasks by due date (normalized to just the date without time)
    const groupTasksByDate = (tasks) => {
        const groups = {};

        tasks.forEach(task => {
            const dateKey = normalizeDateKey(task.dueDate, task.executionMode);

            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(task);
        });

        // Sort date keys chronologically
        return Object.keys(groups)
            .sort((a, b) => {
                if (a === b) return 0;
                if (a === '0000-00-00_immediate') return -1;
                if (b === '0000-00-00_immediate') return 1;
                if (a === '9999-99-99_no_date') return 1;
                if (b === '9999-99-99_no_date') return -1;
                return a.localeCompare(b);
            })
            .map(dateKey => ({
                date: dateKey,
                tasks: groups[dateKey].sort(sortByDate)
            }));
    };

    // Split tasks by type (pure automations vs manual/new-hire tasks)
    const splitByType = (tasks) => {
        const automated = tasks.filter(t => isAutomatedTask(t));
        const manual = tasks.filter(t => !isAutomatedTask(t));
        return { automated, manual };
    };

    // Build the workflow structure with proper grouping and branching
    const workflowStructure = useMemo(() => {
        if (stageTasks.length === 0) {
            return { groups: [], hasAnyBranching: false };
        }

        const dateGroups = groupTasksByDate(stageTasks);
        const processedGroups = [];
        let hasAnyBranching = false;

        dateGroups.forEach((group, groupIndex) => {
            const { automated, manual } = splitByType(group.tasks);
            const hasBranching = automated.length > 0 && manual.length > 0;

            if (hasBranching) {
                hasAnyBranching = true;
            }

            processedGroups.push({
                date: group.date,
                hasBranching,
                automated,
                manual,
                allTasks: group.tasks,
                isFirst: groupIndex === 0,
                isLast: groupIndex === dateGroups.length - 1
            });
        });

        return { groups: processedGroups, hasAnyBranching };
    }, [stageTasks, groupTasksByDate, splitByType]);

    // Calculate stage progress for timeline
    const stageProgress = useMemo(() => {
        const progress = {};
        stages.forEach(stage => {
            const stageData = hire?.tasks?.[stage];
            if (!stageData) {
                progress[stage] = { total: 0, completed: 0 };
                return;
            }

            let total = 0;
            let completed = 0;

            // Handle workflow format
            if (stageData.workflow?.tasks) {
                Object.values(stageData.workflow.tasks).forEach(task => {
                    total++;
                    if (task.status === 'completed') completed++;
                });
            } else {
                // Legacy format
                Object.entries(stageData)
                    .filter(([key]) => key !== 'workflow')
                    .forEach(([, task]) => {
                        total++;
                        if (task.status === 'completed') completed++;
                    });
            }

            progress[stage] = { total, completed };
        });
        return progress;
    }, [hire, stages]);

    const handleTaskMenuAction = (action, task) => {
        switch (action) {
            case 'edit':
                if (!canManageTasks) return;
                onEditTask?.(task);
                break;
            case 'complete':
            case 'uncomplete':
                if (!canToggleTaskCompletion?.(task, action === 'complete')) return;
                onTaskAction?.(action, task);
                break;
            case 'delete':
                if (!canManageTasks) return;
                onDeleteTask?.(task);
                break;
            case 'upload_docs':
                if (!canManageTasks) return;
                onTaskAction?.('upload_docs', task);
                break;
            case 'edit_email':
                if (!canManageTasks) return;
                onTaskAction?.('edit_email', task);
                break;
            default:
                onTaskAction?.(action, task);
        }
    };

    const handleToggleComplete = (task, newState) => {
        if (!canToggleTaskCompletion?.(task, newState)) return;
        onTaskAction?.(newState ? 'complete' : 'uncomplete', task);
    };

    // Render a group of tasks (either as shared cell or individual nodes)
    const renderTaskGroup = (tasks) => {
        if (tasks.length === 0) return null;

        return (
            <SharedTaskCell
                tasks={tasks}
                onMenuAction={handleTaskMenuAction}
                onToggleComplete={handleToggleComplete}
                canManageTask={canManageTasks}
                canToggleTaskCompletion={canToggleTaskCompletion}
            />
        );
    };

    return (
        <div className="onboarding-workflow-view">
            {/* Stage Timeline */}
            <OnboardingStageTimeline
                stages={stages}
                activeStage={activeStage}
                onStageClick={onStageChange}
                startDate={hire?.startDate}
                stageProgress={stageProgress}
                onAddAutomatedTask={onAddAutomatedTask}
                onAddHumanTask={onAddHumanTask}
                canAddTasks={canManageTasks}
            />

            {/* Workflow Canvas */}
            <div className="workflow-canvas">
                {workflowStructure.groups.length === 0 ? (
                    <div className="workflow-empty-state">
                        <i className="fa-solid fa-sitemap" />
                        <p>{t('workflow.noTasksInStageYet', 'No tasks in this stage yet')}</p>
                        {canManageTasks && (
                            <AddTaskButton
                                onClick={() => onAddTask?.(activeStage)}
                                label={t('buttons.addFirstTask', 'Add First Task')}
                            />
                        )}
                    </div>
                ) : (
                    <>
                        {workflowStructure.groups.map((group, groupIndex) => (
                            <React.Fragment key={group.date}>
                                {/* Connector from previous group (except for first) */}
                                {groupIndex > 0 && (
                                    <WorkflowConnector type="vertical" height={30} width={320} />
                                )}

                                {group.hasBranching ? (
                                    /* Branching layout: automated (left) vs manual (right) */
                                    <>
                                        {/* Branch split indicator */}
                                        <WorkflowConnector type="branch-split" height={50} width={550} />

                                        {/* Parallel branches */}
                                        {/* Parallel branches */}
                                        <div className="parallel-branch-container">
                                            {/* Left Branch - Automated Tasks */}
                                            {group.automated.length > 0 && (
                                                <div className="branch-column left">
                                                    {renderTaskGroup(group.automated)}
                                                </div>
                                            )}

                                            {/* Right Branch - Manual and new-hire tasks */}
                                            {group.manual.length > 0 && (
                                                <div className="branch-column right">
                                                    {renderTaskGroup(group.manual)}
                                                </div>
                                            )}
                                        </div>

                                        {/* Merge point after parallel branches */}
                                        <div className="merge-point-container">
                                            <WorkflowConnector type="merge-left" height={50} width={225} />
                                            {/* Diamond Removed */}
                                            <WorkflowConnector type="merge-right" height={50} width={225} />
                                        </div>
                                    </>
                                ) : (
                                    /* Single column - all tasks for this date grouped together */
                                    <div className="workflow-single-column">
                                        {renderTaskGroup(group.allTasks)}
                                    </div>
                                )}
                            </React.Fragment>
                        ))}

                    </>
                )}
            </div>
        </div>
    );
};

export default OnboardingWorkflowView;
